import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash2, Plus, RotateCcw, ExternalLink } from 'lucide-react';
import api from '../../lib/api';
import { LEGAL_DEFAULTS } from '../../lib/legalDefaults';

const PATHS = { conditions: '/conditions', mentions: '/mentions-legales', confidentialite: '/confidentialite' };
const clone = (p) => ({ kicker: p.kicker, title: p.title, blocks: p.blocks.map((b) => ({ ...b })) });
const btn = 'w-8 h-8 flex items-center justify-center border border-[#111111]/20 hover:border-[#111111] disabled:opacity-30';

const BlockEditor = ({ b, i, n, onChange, onMove, onRemove }) => (
  <div className="border border-[#111111]/10 p-4 md:p-5 bg-white" data-testid={`legal-edit-block-${i}`}>
    <div className="flex items-center justify-between gap-3 mb-3">
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B6B6B]">Rubrique {i + 1}</span>
      <div className="flex gap-1">
        <button type="button" className={btn} disabled={i === 0} onClick={() => onMove(-1)} aria-label="Monter" data-testid={`legal-block-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
        <button type="button" className={btn} disabled={i === n - 1} onClick={() => onMove(1)} aria-label="Descendre" data-testid={`legal-block-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
        <button type="button" className={`${btn} hover:border-red-500 hover:text-red-500`} onClick={onRemove} aria-label="Supprimer la rubrique" data-testid={`legal-block-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
    <input value={b.title} onChange={(e) => onChange({ ...b, title: e.target.value })} placeholder="Titre de la rubrique" className="luxury-input-light mb-3" data-testid={`legal-block-title-${i}`} />
    <textarea rows={5} value={b.body} onChange={(e) => onChange({ ...b, body: e.target.value })} className="luxury-input-light resize-y" data-testid={`legal-block-body-${i}`} />
  </div>
);

const LegalManager = () => {
  const [key, setKey] = useState('conditions');
  const [draft, setDraft] = useState(null);
  const [custom, setCustom] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(null);
    api.get(`/legal/${key}`).then(({ data }) => { setCustom(!!data?.title); setDraft(clone(data?.title ? data : LEGAL_DEFAULTS[key])); })
      .catch(() => { setCustom(false); setDraft(clone(LEGAL_DEFAULTS[key])); });
  }, [key]);

  const setBlock = (i, b) => setDraft((d) => ({ ...d, blocks: d.blocks.map((x, j) => (j === i ? b : x)) }));
  const move = (i, dir) => setDraft((d) => { const bl = [...d.blocks]; [bl[i], bl[i + dir]] = [bl[i + dir], bl[i]]; return { ...d, blocks: bl }; });
  const remove = (i) => window.confirm('Supprimer cette rubrique ?') && setDraft((d) => ({ ...d, blocks: d.blocks.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!draft.title.trim()) { toast.error('Le titre de la page est requis'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/legal/${key}`, { ...draft, blocks: draft.blocks.filter((b) => b.title.trim() || b.body.trim()) });
      setCustom(true);
      toast.success('Page enregistrée');
    } catch (err) { toast.error(err?.response?.status === 422 ? 'Texte trop long ou trop de rubriques (titre 200 caractères, rubrique 10 000 caractères, 40 rubriques maximum).' : 'Erreur lors de l’enregistrement'); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm('Rétablir le texte d’origine de cette page ?')) return;
    try { await api.delete(`/admin/legal/${key}`); setCustom(false); setDraft(clone(LEGAL_DEFAULTS[key])); toast.success('Texte d’origine rétabli'); } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="admin-legal-manager">
      <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Informations légales</span>
      <h2 className="text-3xl md:text-4xl font-serif text-[#111111]">Pages légales</h2>
      <p className="text-sm text-[#6B6B6B] mt-3 mb-8 max-w-2xl leading-relaxed">
        Une ligne vide sépare deux paragraphes. Une ligne qui commence par « - » devient un élément de liste. Les adresses e-mail deviennent des liens.
      </p>
      <div className="flex flex-wrap gap-2 mb-8">
        {Object.entries(LEGAL_DEFAULTS).map(([k, p]) => (
          <button key={k} onClick={() => setKey(k)} data-testid={`legal-page-${k}`}
            className={`px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] border transition-colors ${key === k ? 'bg-[#111111] text-white border-[#111111]' : 'border-[#111111]/20 hover:border-[#111111]'}`}>{p.label}</button>
        ))}
      </div>
      {!draft ? <p className="text-[#6B6B6B] py-10">Chargement…</p> : (
        <div className="space-y-5 max-w-3xl">
          <a href={PATHS[key]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6B6B6B] hover:text-[#111111]">Voir la page <ExternalLink className="w-3 h-3" /></a>
          <label className="block"><span className="text-[10px] uppercase tracking-[0.25em] text-[#6B6B6B] mb-2 block">Surtitre</span>
            <input value={draft.kicker} onChange={(e) => setDraft({ ...draft, kicker: e.target.value })} className="luxury-input-light" data-testid="legal-kicker-input" /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-[0.25em] text-[#6B6B6B] mb-2 block">Titre *</span>
            <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="luxury-input-light" data-testid="legal-title-input" /></label>
          {draft.blocks.map((b, i) => (
            <BlockEditor key={i} b={b} i={i} n={draft.blocks.length} onChange={(nb) => setBlock(i, nb)} onMove={(d) => move(i, d)} onRemove={() => remove(i)} />
          ))}
          <button type="button" onClick={() => setDraft({ ...draft, blocks: [...draft.blocks, { title: '', body: '' }] })} data-testid="legal-add-block"
            className="flex items-center gap-2 px-5 py-3 border border-dashed border-[#111111]/40 text-xs uppercase tracking-[0.2em] hover:border-[#111111]"><Plus className="w-4 h-4" /> Ajouter une rubrique</button>
          <div className="flex flex-wrap gap-3 pt-4">
            <button onClick={save} disabled={saving} data-testid="legal-save" className="px-6 py-3 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] disabled:opacity-40">{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
            {custom && <button onClick={reset} data-testid="legal-reset" className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.3em] border border-[#111111]/20 hover:border-[#111111]"><RotateCcw className="w-3.5 h-3.5" /> Texte d’origine</button>}
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalManager;
